import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Alert, useTheme } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useDispatch } from "react-redux";
import { setUserData, setDefaultStation } from "slices/userSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import AuthLayout, { authInputSx, authFieldLabelSx, authButtonSx } from "components/AuthLayout";
import { markSuccessfulLogin } from "api/config";

const baseUrl = process.env.REACT_APP_BASE_URL + "/public";
// Only the username is remembered; the password field is a masked text input
// so browsers don't offer to save/autofill the password.
const LAST_EMAIL_KEY = "lastLoginEmail";

export default function UserLogin() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Enhanced error state management
  const [error, setError] = useState({
    show: false,
    message: '',
    severity: 'error',
    code: null
  });

  // Rate limiting state
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lastEmail] = useState(() => localStorage.getItem(LAST_EMAIL_KEY) || "");

  // Countdown timer for rate limiting
  useEffect(() => {
    let timer;
    if (isRateLimited && retryCountdown > 0) {
      timer = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            setIsRateLimited(false);
            setError({
              show: false,
              message: '',
              severity: 'error',
              code: null
            });
            return 0;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute
    }
    return () => clearInterval(timer);
  }, [isRateLimited, retryCountdown]);

  // Clear error when user starts typing
  const clearError = () => {
    if (error.show && !isRateLimited) {
      setError({
        show: false,
        message: '',
        severity: 'error',
        code: null
      });
    }
  };

  const setUserState = (userData, defaultStation) => {
    dispatch(setUserData(userData));
    dispatch(setDefaultStation(defaultStation));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError({ show: false, message: '', severity: 'error', code: null });

    const data = new FormData(event.currentTarget);

    try {
      const res = await axios.post(baseUrl + "/login", {
        email: data.get("email"),
        password: data.get("password"),
      }, {
        withCredentials: true // Enable cookies for authentication
      });

      if (res && res.status === 200 && res.data.success) {
        setUserState(res.data.user, res.data.defaultStation);
        console.log(res.data);

        // Mark successful login to prevent immediate logout
        markSuccessfulLogin();
        localStorage.setItem(LAST_EMAIL_KEY, data.get("email") || "");

        // Show success message
        setError({
          show: true,
          message: res.data.message || "Login successful!",
          severity: 'success',
          code: 'SUCCESS'
        });

        // Tokens are now in httpOnly cookies, only store non-sensitive data
        if (res.data.user) {
          const sanitizedUser = { ...res.data.user };
          delete sanitizedUser.password;
          delete sanitizedUser.token;
          localStorage.setItem("user", JSON.stringify(sanitizedUser));
        }
        if (res.data.defaultStation) {
          localStorage.setItem(
            "defaultStation",
            JSON.stringify(res.data.defaultStation)
          );
        }

        // Redirect after a brief success message (increased delay to ensure cookies are set)
        // Wait 30 seconds before redirecting to /scan so user can inspect backend response
        setTimeout(() => {
          window.location.replace("/scan");
        }, 2000);
      }
    } catch (error) {
      console.log("Login error:", error);

      // Enhanced error handling based on backend response
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorCode = errorData.code;

        switch (errorCode) {
          case 'RATE_LIMITED':
            setIsRateLimited(true);
            setRetryCountdown(errorData.retryAfter || 15);
            setError({
              show: true,
              message: `${errorData.message} (${errorData.retryAfter || 15} minutes remaining)`,
              severity: 'warning',
              code: errorCode
            });
            break;

          case 'MISSING_CREDENTIALS':
            setError({
              show: true,
              message: errorData.message,
              severity: 'error',
              code: errorCode
            });
            break;

          case 'INVALID_EMAIL_FORMAT':
            setError({
              show: true,
              message: errorData.message,
              severity: 'error',
              code: errorCode
            });
            break;

          case 'INVALID_CREDENTIALS':
            setError({
              show: true,
              message: errorData.message,
              severity: 'error',
              code: errorCode
            });
            break;

          case 'SERVER_ERROR':
            setError({
              show: true,
              message: errorData.message,
              severity: 'error',
              code: errorCode
            });
            break;

          default:
            // Fallback for any other error
            setError({
              show: true,
              message: errorData.message || "An error occurred during login. Please try again.",
              severity: 'error',
              code: 'UNKNOWN_ERROR'
            });
        }
      } else {
        // Network or other errors
        setError({
          show: true,
          message: "Unable to connect to the server. Please check your internet connection and try again.",
          severity: 'error',
          code: 'NETWORK_ERROR'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    // If the API interceptor force-logged-out due to an expired session
    // (see api/config.js forceLogout), show why the user landed back here.
    if (sessionStorage.getItem("sessionExpired")) {
      sessionStorage.removeItem("sessionExpired");
      setError({
        show: true,
        message: t("adminLogin.sessionExpired"),
        severity: "warning",
        code: "SESSION_EXPIRED"
      });
    }
  }, [t]);

  React.useEffect(() => {
    // Check if user is already authenticated by checking stored user data
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData && userData.id) {
          navigate("/tracking");
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem("user");
      }
    }
  }, [navigate]);

  const inputSx = authInputSx(theme);
  const fieldLabelSx = authFieldLabelSx(theme);

  const features = [
    t("adminLogin.feature1"),
    t("adminLogin.feature2"),
    t("adminLogin.feature3"),
  ];

  return (
    <AuthLayout
      badge={{ icon: <ShieldOutlinedIcon sx={{ fontSize: 14, color: "#f59e0b" }} />, label: t("adminLogin.badge") }}
      tagline={t("adminLogin.tagline")}
      features={features}
    >
      <Box sx={{ mb: "34px" }}>
        <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 30, fontWeight: 700, color: "#1b1728", lineHeight: 1.2, mb: 1 }}>
          {t("adminLogin.title")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#9b8ab8", fontWeight: 500 }}>
          {t("adminLogin.subtitle")}
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Typography component="label" htmlFor="email" sx={fieldLabelSx}>
            {t("adminLogin.emailLabel")}
          </Typography>
          <TextField
            required
            fullWidth
            id="email"
            name="email"
            placeholder={t("adminLogin.emailPlaceholder")}
            autoComplete="username"
            autoFocus={!lastEmail}
            defaultValue={lastEmail}
            type="email"
            onChange={clearError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ mb: "14px" }}>
          <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
            {t("adminLogin.passwordLabel")}
          </Typography>
          <TextField
            required
            fullWidth
            id="password"
            name="password"
            placeholder={t("adminLogin.passwordPlaceholder")}
            type="text"
            autoComplete="off"
            autoFocus={!!lastEmail}
            inputProps={{ autoCapitalize: "off", autoCorrect: "off", spellCheck: false }}
            onChange={clearError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, "& input": { WebkitTextSecurity: showPassword ? "none" : "disc" } }}
          />
        </Box>

        {error.show && (
          <Alert severity={error.severity} sx={{ mb: 2 }}>
            {error.message}
            {isRateLimited && retryCountdown > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {t("adminLogin.waitBeforeRetry", { count: retryCountdown })}
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Link
            href="#"
            variant="body2"
            sx={{
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
              color: theme.palette.secondary.main,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("adminLogin.forgotPassword")}
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={isLoading || isRateLimited}
          sx={authButtonSx(theme)}
        >
          {isLoading
            ? t("adminLogin.signingIn")
            : isRateLimited
              ? t("adminLogin.waitMinutes", { count: retryCountdown })
              : t("adminLogin.signIn").toUpperCase()}
        </Button>
      </Box>
    </AuthLayout>
  );
}
